import React from 'react';
import {
	Button,
	fromThemeColors,
	layout,
	palette,
	styleUtils,
	withModals,
	WithModalsProps,
} from '@citrite/web-ui-component';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useDesktopDetails } from 'App/Activity/useDesktopDetails';
import { GenericErrorBoundary } from 'App/GenericErrorBoundary';
import { DetailedDesktopTileFooter } from 'App/Tile/DetailedDesktopTileFooter';
import { DetailedDesktopTileHeader } from 'App/Tile/DetailedDesktopTileHeader';
import { DivWrapper } from 'App/Tile/GenericTile';
import { useBrowserExtensionPrompt } from 'App/Tile/useBrowserExtensionPrompt';
import { launchResourceWithPrompts, shouldShowTroubleshootOption } from 'App/Tile/utils';
import { useConfigurationContext } from 'Configuration/useConfigurationContext';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { LaunchStatus } from 'Environment/launchResource/launchResource';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import { useUserContext } from 'Workspace/UserContext';

const buttonWrapperStyle = {
	width: '100%',
};

const StyledDiv = styled.div<{ isResourceAvailable: boolean; shouldGrayOut: boolean }>`
	display: flex;
	flex-direction: column;
	min-width: 344px;
	height: 352px;
	border-radius: ${layout.space12};
	border: 1px solid ${palette.grey300};
	background: ${fromThemeColors('primaryContainerBackground')};
	cursor: pointer;

	${({ shouldGrayOut }) =>
		shouldGrayOut
			? css`
					box-shadow: ${styleUtils.shadow1.boxShadow};
					&:hover {
						box-shadow: ${styleUtils.shadow2.boxShadow};
					}
					.resource-icon-image {
						filter: grayscale(100%) saturate(0) brightness(92%) contrast(88%)
							drop-shadow(0 1px 0 rgba(0, 0, 0, 0.12))
							drop-shadow(0 0 2px rgba(0, 0, 0, 0.08));
					}
			  `
			: css`
					box-shadow: ${styleUtils.shadow2.boxShadow};
					&:hover {
						transition: box-shadow 0.1s linear;
						box-shadow: ${styleUtils.shadow3.boxShadow};
					}
			  `}

	${({ isResourceAvailable }) =>
		!isResourceAvailable &&
		css`
			opacity: 0.5;
			cursor: not-allowed;
			box-shadow: ${styleUtils.shadow1.boxShadow};
			&:hover {
				box-shadow: ${styleUtils.shadow1.boxShadow};
			}
			* {
				pointer-events: none;
			}
		`}
`;
interface Props {
	resource: Resource;
	isResourceAvailable: boolean;
}

interface __Props extends Props, WithModalsProps {}

function __DetailedDesktopTile({ resource, isResourceAvailable, showModal }: __Props) {
	const shouldWrapButtonComponent = !useFeatureCanary(
		FeatureFlag.DisableResourceButtonWrap
	);
	const favoriteRef = React.useRef(null);
	const { isUnderMaintenanceOrUnavailable } = useDesktopDetails(resource);
	const { value: resourceContext } = useLoadableResourceContext();
	const { workspaceConfiguration } = useConfigurationContext();
	const { userDetails } = useUserContext();
	const canShowBrowserExtensionPrompt = useBrowserExtensionPrompt();

	const canShowTroubleshootStatus =
		shouldShowTroubleshootOption(resource, workspaceConfiguration) &&
		resourceContext?.resourceLatestStatus?.[resource.id]?.status === LaunchStatus.Failed;

	const handleLaunchResource = () =>
		launchResourceWithPrompts({
			workspaceConfiguration,
			resource,
			resourceContext,
			userDetails,
			canShowBrowserExtensionPrompt,
			showModal,
		});
	const Wrapper = shouldWrapButtonComponent ? Button.Wrapper : DivWrapper;

	return (
		<Wrapper
			disabled={!isResourceAvailable}
			onClick={handleLaunchResource}
			style={buttonWrapperStyle}
		>
			<StyledDiv
				isResourceAvailable={isResourceAvailable}
				shouldGrayOut={isUnderMaintenanceOrUnavailable}
				className="detailed-desktop-tile-container"
				data-testid={resource.name}
			>
				<DetailedDesktopTileHeader
					resource={resource}
					favoriteRef={favoriteRef}
					canShowTroubleshootStatus={canShowTroubleshootStatus}
				/>
				<DetailedDesktopTileFooter
					resource={resource}
					showModal={showModal}
					favoriteRef={favoriteRef}
				/>
			</StyledDiv>
		</Wrapper>
	);
}

const _DetailedDesktopTile = withModals(__DetailedDesktopTile);

export const DetailedDesktopTile = (props: Props) => {
	return (
		<GenericErrorBoundary
			renderProp={() => <_DetailedDesktopTile {...props} />}
			name="DetailedDesktopTile"
		/>
	);
};
